// Purpose: loading-state primitives — Skeleton, Spinner, ProgressBar,
// ProgressFill. Industrial feel: no rainbow spinners, no bouncy progress.

import { useLayoutEffect, useRef } from 'react';
import type { HTMLAttributes } from 'react';
import { LoaderIcon } from '../icons/index.tsx';

/** Skeleton block for card/list/table row placeholders. Shimmer gradient
    respects prefers-reduced-motion (styles.css handles disable). */
export function Skeleton({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      aria-hidden
      className={[
        'rounded-sm',
        'bg-[linear-gradient(90deg,var(--bg-tertiary)_0%,var(--bg-secondary)_50%,var(--bg-tertiary)_100%)]',
        'bg-[length:200%_100%]',
        'animate-shimmer',
        className ?? '',
      ].join(' ')}
      {...rest}
    />
  );
}

/** Inline spinner for button loading + page transitions. */
export function Spinner({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <LoaderIcon
      size={size}
      className={['animate-spin', className ?? ''].join(' ')}
      aria-label="Loading"
    />
  );
}

/**
 * The animated fill of a determinate progress bar. Width is driven by
 * the `--progress` CSS custom property on the element itself, set via
 * `useLayoutEffect` + `ref.style.setProperty('--progress', ...)` so the
 * rendered DOM carries no `style="..."` attribute. This pattern is what
 * lets the production CSP keep `style-src 'self'` (no `'unsafe-inline'`)
 * while still rendering arbitrary per-frame widths. Future progress
 * bars MUST go through this primitive instead of using `style={{ width
 * }}` directly — or the CSP will start blocking them.
 *
 * Wrap in your own track element (with `role="progressbar"` + the
 * aria attributes) when you need accessibility semantics; this
 * primitive is presentation-only.
 */
export interface ProgressFillProps extends HTMLAttributes<HTMLDivElement> {
  /** Percentage in [0, 100]. Clamped internally. */
  value: number;
}

export function ProgressFill({ value, className, ...rest }: ProgressFillProps) {
  const ref = useRef<HTMLDivElement>(null);
  const pct = Math.max(0, Math.min(100, value));
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) el.style.setProperty('--progress', `${pct}%`);
  }, [pct]);
  return <div ref={ref} className={['progress-fill', className ?? ''].join(' ')} {...rest} />;
}

/** Determinate progress bar. value ∈ [0, 100]. */
export function ProgressBar({
  value,
  label,
  className,
}: {
  value: number;
  label?: string;
  className?: string;
}) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className={[
        'h-1 w-full rounded-full bg-border-default overflow-hidden',
        className ?? '',
      ].join(' ')}
    >
      <ProgressFill
        value={pct}
        className="h-full bg-accent-primary transition-[width] duration-base ease-out"
      />
    </div>
  );
}
